import { useState } from 'react'

import {
  Operator,
  PAST_EVENTS_ISSUE_STATE,
  PastEvent,
  PastEventsIssueState
} from '@img-arena/consoles-api-streaming'
import {
  extendFiltersFreeTextData,
  PaginatedResultType
} from '@/components/inputs/Autocomplete/utils'
import { Column, COLUMN_CELL_TYPE, Filter } from '@/components/table/base'
import { FilterType } from '@/components/table/base/FilterableTable/FilterableTable.types'
import { DetailedCell } from '@/components/table/base/TableBase/components/DetailedCell'
import { streamingRestApi } from '@/service/streamingApi'
import type { PastEventsSortAttribute } from '@/stores/pastEvents/pastEvents.store'
import { formatDate } from '@/utils/dateFormat/dateFormat'

type IssueStatusOption = {
  label: string
  value: PastEventsIssueState
}

const STATE_MAP: IssueStatusOption[] = [
  {
    label: 'Has Issues',
    value: PAST_EVENTS_ISSUE_STATE.ISSUE
  },
  {
    label: 'No Issues',
    value: PAST_EVENTS_ISSUE_STATE.NO_ISSUE
  },
  {
    label: 'No selection',
    value: PAST_EVENTS_ISSUE_STATE.NO_SELECTION
  }
]

export const PAST_EVENTS_COLUMNS: Column<PastEvent, PastEventsSortAttribute>[] = [
  {
    id: '$name',
    label: 'Name',
    type: COLUMN_CELL_TYPE.COMPONENT,
    sx: { width: 400 },
    render: (value) => (
      <DetailedCell
        value={value.eventTitle}
        subValue={`[${value.sportName.toLocaleUpperCase()}] ${value.tournamentName}`}
      />
    )
  },
  {
    id: 'startDate',
    label: 'Start Date',
    type: COLUMN_CELL_TYPE.COMPONENT,
    render: (value) => {
      const { time, fullDate, utcOffset } = formatDate(value)
      return <DetailedCell value={time} subValue={`${fullDate} ${utcOffset}`} />
    }
  },
  {
    id: 'endDate',
    label: 'End Date',
    type: COLUMN_CELL_TYPE.COMPONENT,
    render: (value) => {
      const { time, fullDate, utcOffset } = formatDate(value)
      return <DetailedCell value={time} subValue={`${fullDate} ${utcOffset}`} />
    }
  }
]

const getOptions = (data: PastEvent[]) => {
  const options = data.reduce(
    (acc: Record<'sportOptions' | 'propertyOptions' | 'tournamentOptions', FilterType[]>, item) => {
      const { propertyName, propertyId, tournamentName, tournamentId, sportName, sportId } = item

      if (!acc.sportOptions[sportId]) {
        acc.sportOptions[sportId] = {
          value: sportId.toString(),
          label: sportName
        }
      }

      if (!acc.propertyOptions[propertyId]) {
        acc.propertyOptions[propertyId] = {
          value: propertyId.toString(),
          label: propertyName
        }
      }

      if (
        (tournamentId !== undefined || tournamentId !== null) &&
        !acc.tournamentOptions[tournamentId]
      ) {
        acc.tournamentOptions[tournamentId] = {
          value: tournamentId.toString(),
          label: tournamentName
        }
      }

      return acc
    },
    {
      sportOptions: [],
      propertyOptions: [],
      tournamentOptions: []
    }
  )

  return options
}

export const pastEventsFilters = (data: PastEvent[]): Filter<PastEvent>[] => {
  const { sportOptions, propertyOptions, tournamentOptions } = getOptions(data)
  const operatorsApi = streamingRestApi.endpoints.operators

  const [loading, setLoading] = useState(false)
  const [operatorsOptions, setOperatorsOptions] = useState<PaginatedResultType<Operator>>({
    data: [],
    pagination: {
      page: 0,
      pageSize: 100,
      total: 0
    }
  })

  const loadMoreCallback = (page?: number, query?: string) => {
    setLoading(true)

    try {
      operatorsApi
        .getAll({
          page,
          pageSize: operatorsOptions.pagination.pageSize,
          query: query ? query : undefined
        })
        .then((response) => {
          setOperatorsOptions((prev) =>
            extendFiltersFreeTextData(prev, {
              data: response.data.content,
              pagination: {
                total: response.data.totalElements,
                page: response.data.number,
                pageSize: response.data.size
              }
            })
          )
        })
    } catch (err) {
      console.error('Error fetching operators:', err)
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  return [
    { type: 'dateRange', paramName: 'dateRange', label: 'Date Range', width: 250 },
    {
      type: 'select',
      paramName: 'state',
      label: 'Event State',
      options: STATE_MAP
    },
    {
      type: 'select',
      paramName: 'sport',
      label: 'Sport',
      options: sportOptions
    },
    { type: 'select', paramName: 'property', label: 'Property', options: propertyOptions },
    { type: 'select', paramName: 'tournament', label: 'Tournament', options: tournamentOptions },
    {
      type: 'autocomplete',
      label: 'Operator',
      paramName: 'operator',
      loadMoreOptions: {
        loading,
        loadMoreCallback,
        pagination: operatorsOptions.pagination
      },
      options: operatorsOptions.data.map((item) => ({ value: item.id, label: item.name })),
      width: 200
    }
  ]
}
